<template>
  <bulk-update-wrapper
    :saving="saving"
    title="Update Pickup Location"
    :button-label="props.buttonLabel"
    :has-changes="hasChanges"
    :show-hint="showHint"
    @save="save"
    @dismiss="dismiss"
  >
    <new-location-picker
      :value="pickupLocationId"
      label="Pickup location"
      data-test="bulk-update-pickup"
      required
      @input="updatePickupLocation"
    />
    <template #error-snackbar>
      <vs-snackbar :value="error" type="error" sticky @input="clearSnackbar">
        Failed to update pickup location
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import BulkUpdateWrapper from '../common/BulkUpdateWrapper.vue'
import NewLocationPicker from '@/components/locations/NewLocationPicker.vue'
import { computed, ref, onMounted } from 'vue'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { InventoryRequirement } from '@/models/types/inventory-requirement'
import { bulkUpdatePickupLocation } from '@/api/jobs'

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

const props = defineProps<{
  buttonLabel: string
  selectedJobs: JobJson[]
}>()

const error = ref(false)
const saving = ref(false)
const pickupLocationId = ref<number | undefined>()
let initialPickupLocationId: number | undefined

onMounted(() => {
  const selectedPickupLocationIds: Set<number> = new Set(
    props.selectedJobs
      .map((job) =>
        job.inventory_requirements.map((inv: InventoryRequirement) => inv.pickup_location_id),
      )
      .flat(),
  )

  if (selectedPickupLocationIds.size === 1) {
    initialPickupLocationId = [...selectedPickupLocationIds][0]
  }
  pickupLocationId.value = initialPickupLocationId
})

const hasChanges = computed<boolean>(() => {
  return pickupLocationId.value !== initialPickupLocationId
})
const showHint = computed<boolean>(() => {
  return !pickupLocationId.value && initialPickupLocationId === undefined
})

const updatePickupLocation = (input: number | number[]) => {
  if (!(input instanceof Array)) {
    pickupLocationId.value = input
  }
}

const clearSnackbar = () => (error.value = false)
const dismiss = () => emits('dismiss')

const save = async () => {
  saving.value = true
  try {
    await bulkUpdatePickupLocation(
      props.selectedJobs.map((j) => j.number),
      pickupLocationId.value,
    )
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, {
        inventory_requirements: job.inventory_requirements.map((inv: InventoryRequirement) => ({
          ...inv,
          pickup_location_id: pickupLocationId,
        })),
      }),
    )
    emits('updated', { message: 'Pickup location updated', updatedJobs })
  } catch (err) {
    error.value = true
  } finally {
    saving.value = false
  }
}
</script>
